/**
 * components/ui/AppSelect.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The dashboard's one dropdown. A drop-in replacement for `<select>`,
 * `<Form.Select>` and `<Form.Control as="select">`.
 *
 * WHY NOT THE NATIVE POPUP: a native select opens the operating system's own
 * menu (the grey macOS list, the Windows box), so the same control looked
 * different on every machine and nothing like the rest of the console. This
 * draws the menu itself, in the dashboard's type and the #083860 action
 * colour, and can show a second line under each option (a role's meaning).
 *
 * HOW IT STAYS A DROP-IN: the real <select> is still rendered, with every prop
 * the call site passed (value, onChange, name, ref, data-*), invisible and
 * inert. Picking an item sets that select's value and dispatches a real
 * `change` event on it, so every existing handler keeps receiving a genuine
 * event whose target is the select: `e.target.value`, `e.target.name`,
 * `e.target.selectedOptions`, react-hook-form's `register`, tests that call
 * `fireEvent.change(select)`, all unchanged. The visible control reads its
 * options and current value back from that select after every render, so
 * `<option>` children can be written any way JSX allows.
 *
 * Two ways to supply options:
 *   <AppSelect value={v} onChange={(e) => setV(e.target.value)}>
 *     <option value="">Choose...</option>
 *     {rows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
 *   </AppSelect>
 *
 *   <AppSelect value={v} onChange={...} placeholder="Choose a role"
 *     options={[{ value: "ORDERS", label: "Orders", description: "Moves orders" }]} />
 *
 * A status pill beside an option (a rider's Online/Offline, a vendor's Open/
 * Closed): `data-badge="Online" data-badge-tone="online"` on the <option>, or
 * `badge` / `badgeTone` in an options entry. The pill sits at the right of the
 * row and beside the chosen value in the closed control, so the state is read
 * at a glance instead of being parsed out of "Name || Offline". Tones:
 * online, offline, info, warn, danger (default: neutral grey).
 *
 * Layout: `style` may position the control (width, margins, flex); visual keys
 * (border, padding, colour, font) are ignored on purpose, so every dropdown
 * looks the same. `block` (or a converted Form.Control) fills its container.
 * `size="sm"` gives the compact 30px control for dense rows.
 *
 * `multiple` and listbox selects (size > 1) are left native, styled to match.
 * Keyboard: arrows, Home/End, Enter/Space, Esc, Tab, and type-to-jump.
 */
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./ui.css";

let idSeq = 0;
const nextId = (prefix) => `${prefix}-${++idSeq}`;

const cx = (...parts) => parts.filter(Boolean).join(" ");

// Style keys that place the control in its layout. Everything else is visual
// and belongs to this component.
const LAYOUT_KEYS = new Set([
  "width", "minWidth", "maxWidth", "flex", "flexGrow", "flexShrink", "flexBasis",
  "alignSelf", "justifySelf", "margin", "marginTop", "marginRight", "marginBottom",
  "marginLeft", "marginInline", "marginBlock", "marginInlineStart", "marginInlineEnd",
  "gridColumn", "gridRow", "gridArea", "order", "verticalAlign",
]);

// Bootstrap form classes that would draw a second border around the control.
const FORM_CLASSES = new Set([
  "form-control", "form-select", "custom-select", "form-control-sm", "form-control-lg",
  "form-select-sm", "form-select-lg", "select-sm",
]);

// react-bootstrap props with no meaning on a plain element.
const BOOTSTRAP_PROPS = ["as", "isValid", "custom", "plaintext", "htmlSize", "bsPrefix", "bsCustomPrefix"];

// A Bootstrap form class means "fill the row" (width: 100%), so a select that
// carried one keeps that width as a block AppSelect.
const splitClassName = (className = "") => {
  let size = null;
  let formClass = false;
  const kept = [];
  for (const c of String(className).split(/\s+/).filter(Boolean)) {
    if (c.endsWith("-sm") && FORM_CLASSES.has(c)) size = "sm";
    if (c.endsWith("-lg") && FORM_CLASSES.has(c)) size = "lg";
    if (FORM_CLASSES.has(c)) { formClass = true; continue; }
    kept.push(c);
  }
  return { size, formClass, kept: kept.join(" ") };
};

const layoutStyle = (style) => {
  if (!style) return undefined;
  const out = {};
  for (const [k, v] of Object.entries(style)) if (LAYOUT_KEYS.has(k)) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

/** Small inline styles (fontSize <= 12 or height <= 32) mean a compact control. */
const impliedSize = (style) => {
  if (!style) return null;
  const n = (v) => (typeof v === "number" ? v : parseFloat(v));
  if ((style.fontSize && n(style.fontSize) <= 12) || (style.height && n(style.height) <= 32)) return "sm";
  return null;
};

const readItems = (select) => {
  const items = [];
  for (const opt of Array.from(select.options)) {
    const parent = opt.parentElement;
    const inGroup = parent && parent.tagName === "OPTGROUP";
    items.push({
      index: opt.index,
      value: opt.value,
      label: (opt.textContent || opt.label || opt.value || "").trim(),
      description: opt.getAttribute("data-description") || "",
      badge: opt.getAttribute("data-badge") || "",
      badgeTone: opt.getAttribute("data-badge-tone") || "",
      disabled: opt.disabled || Boolean(inGroup && parent.disabled),
      hidden: opt.hidden,
      group: inGroup ? parent.label : null,
    });
  }
  return items;
};

const signature = (items, selectedIndex) =>
  JSON.stringify([selectedIndex, items.map((i) => [i.value, i.label, i.description, i.badge, i.badgeTone, i.disabled, i.hidden, i.group])]);

const nativeValueSetter = () => {
  const desc = typeof window !== "undefined"
    ? Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value")
    : null;
  return desc && desc.set;
};

/** The portal host: inside a Bootstrap modal (which steals focus that leaves
 *  it), otherwise <body>. */
const portalHostFor = (el) => (el && el.closest && el.closest(".modal")) || document.body;

const MENU_MAX_HEIGHT = 320;
const SEARCH_THRESHOLD = 10;
const SIZER_LIMIT = 80;

const renderOptionElements = (options, placeholder) => {
  const els = [];
  if (placeholder !== undefined && placeholder !== null) {
    els.push(<option key="__placeholder" value="" hidden>{placeholder}</option>);
  }
  let group = null;
  let groupChildren = [];
  const flush = () => {
    if (group !== null) {
      els.push(<optgroup key={`g-${group}`} label={group}>{groupChildren}</optgroup>);
    }
    group = null;
    groupChildren = [];
  };
  for (const o of options || []) {
    const opt = (
      <option key={String(o.value)} value={o.value} disabled={o.disabled} data-description={o.description || undefined} data-badge={o.badge || undefined} data-badge-tone={o.badgeTone || undefined}>
        {o.label}
      </option>
    );
    if (o.group) {
      if (group !== o.group) { flush(); group = o.group; }
      groupChildren.push(opt);
    } else {
      flush();
      els.push(opt);
    }
  }
  flush();
  return els;
};

const Chevron = () => (
  <svg className="lk-select__chevron" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Badge = ({ text, tone }) =>
  text ? <span className={cx("lk-select__badge", tone && `lk-select__badge--${tone}`)}>{text}</span> : null;

const Check = () => (
  <svg className="lk-select__check" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AppSelect = forwardRef(function AppSelect(props, forwardedRef) {
  const {
    children,
    options,
    placeholder,
    className,
    style,
    size: sizeProp,
    block,
    invalid,
    isInvalid,
    searchable,
    menuMinWidth,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...rest
  } = props;
  for (const k of BOOTSTRAP_PROPS) delete rest[k];

  const numericSize = typeof sizeProp === "number" || /^\d+$/.test(String(sizeProp || ""));
  const isNativeOnly = rest.multiple || (numericSize && Number(sizeProp) > 1);

  const { size: classSize, formClass, kept } = splitClassName(className);
  const size = (!numericSize && sizeProp) || classSize || impliedSize(style) || "md";
  const isBlock = Boolean(block) || formClass || /\bw-100\b/.test(kept) || style?.width === "100%";
  // A select hidden by its call site stays hidden.
  const isHidden = style?.display === "none" || Boolean(rest.hidden);
  const showInvalid = Boolean(invalid || isInvalid);
  const disabled = Boolean(rest.disabled);

  const nativeRef = useRef(null);
  const triggerRef = useRef(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const sigRef = useRef("");
  const typeRef = useRef({ text: "", at: 0 });
  const idsRef = useRef(null);
  if (!idsRef.current) idsRef.current = { trigger: nextId("lk-select"), list: nextId("lk-select-list") };
  const ids = idsRef.current;

  const [snap, setSnap] = useState({ items: [], selectedIndex: -1 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState(null);
  const [labelledBy, setLabelledBy] = useState(undefined);

  const setNativeRef = useCallback((node) => {
    nativeRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  // Read options and the current value back from the real select.
  const sync = useCallback(() => {
    const sel = nativeRef.current;
    if (!sel || !sel.options) return;
    const items = readItems(sel);
    const sig = signature(items, sel.selectedIndex);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setSnap({ items, selectedIndex: sel.selectedIndex });
    }
  }, []);
  useLayoutEffect(() => { sync(); });

  // Uncontrolled selects, and code that writes select.value directly.
  useEffect(() => {
    const sel = nativeRef.current;
    if (!sel || isNativeOnly) return undefined;
    const onChange = () => sync();
    // A <label htmlFor> click (or form validation) focuses the hidden select:
    // hand focus to the visible control instead.
    const onFocus = () => { if (triggerRef.current && !triggerRef.current.disabled) triggerRef.current.focus(); };
    sel.addEventListener("change", onChange);
    sel.addEventListener("focus", onFocus);
    return () => {
      sel.removeEventListener("change", onChange);
      sel.removeEventListener("focus", onFocus);
    };
  }, [sync, isNativeOnly]);

  // Name the control after its <label for=id>, when the page has one.
  useLayoutEffect(() => {
    if (ariaLabelledBy || ariaLabel || !rest.id) return;
    const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(rest.id) : String(rest.id).replace(/"/g, '\\"');
    const label = document.querySelector(`label[for="${escaped}"]`);
    if (label) {
      if (!label.id) label.id = `${rest.id}-label`;
      setLabelledBy(label.id);
    }
  }, [rest.id, ariaLabel, ariaLabelledBy]);

  const { items, selectedIndex } = snap;
  const visible = items.filter((i) => !i.hidden);
  const q = query.trim().toLowerCase();
  const filtered = q ? visible.filter((i) => i.label.toLowerCase().includes(q) || String(i.value).toLowerCase().includes(q)) : visible;
  const selected = selectedIndex >= 0 ? items.find((i) => i.index === selectedIndex) : null;
  const display = selected ? selected.label : (placeholder || "");
  const isPlaceholder = !selected || selected.value === "";
  const useSearch = searchable !== undefined ? Boolean(searchable) : visible.length > SEARCH_THRESHOLD;

  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) { setOpen(false); return; }
    const below = window.innerHeight - r.bottom - 8;
    const above = r.top - 8;
    const wanted = Math.min(MENU_MAX_HEIGHT, (menuRef.current && menuRef.current.scrollHeight) || MENU_MAX_HEIGHT);
    const upward = below < Math.min(wanted, 200) && above > below;
    const maxHeight = Math.max(120, Math.min(MENU_MAX_HEIGHT, upward ? above : below));
    const minWidth = Math.max(r.width, menuMinWidth || 0);
    const maxWidth = Math.max(minWidth, 420);
    let left = r.left;
    if (left + minWidth > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - minWidth);
    setPos(upward
      ? { left, bottom: window.innerHeight - r.top + 4, minWidth, maxWidth, maxHeight, upward: true }
      : { left, top: r.bottom + 4, minWidth, maxWidth, maxHeight, upward: false });
  }, [menuMinWidth]);

  const openMenu = useCallback((startAt) => {
    if (disabled) return;
    setQuery("");
    const first = visible.findIndex((i) => !i.disabled);
    const current = visible.findIndex((i) => i.index === selectedIndex);
    setActive(startAt === "last"
      ? visible.length - 1
      : current >= 0 ? current : first);
    place();
    setOpen(true);
  }, [disabled, visible, selectedIndex, place]);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setQuery("");
    if (refocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  const commit = useCallback((item) => {
    if (!item || item.disabled) return;
    const sel = nativeRef.current;
    close(true);
    if (!sel || sel.value === item.value) return;
    const setter = nativeValueSetter();
    if (setter) setter.call(sel, item.value);
    else sel.value = item.value;
    sel.dispatchEvent(new Event("input", { bubbles: true }));
    sel.dispatchEvent(new Event("change", { bubbles: true }));
    sync();
  }, [close, sync]);

  // While open: keep the menu glued to the trigger, close on outside press.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      close(false);
    };
    const onMove = (e) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      place();
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, close, place]);

  // Place again once the menu has its real height; focus search when shown.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    if (useSearch && searchRef.current) searchRef.current.focus();
  }, [open, place, useSearch]);

  // Keep the active option in view.
  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-i="${active}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const list = open ? filtered : visible;
  const move = (delta) => {
    if (!list.length) return;
    let i = active;
    for (let step = 0; step < list.length; step++) {
      i = (i + delta + list.length) % list.length;
      if (!list[i].disabled) break;
    }
    setActive(i);
  };

  const typeahead = (key) => {
    const now = Date.now();
    const t = typeRef.current;
    t.text = now - t.at > 700 ? key.toLowerCase() : t.text + key.toLowerCase();
    t.at = now;
    const start = open ? active + 1 : 0;
    const ordered = [...visible.slice(start), ...visible.slice(0, start)];
    const hit = ordered.find((i) => !i.disabled && i.label.toLowerCase().startsWith(t.text));
    if (!hit) return;
    if (open) setActive(visible.indexOf(hit));
    else commitQuietly(hit);
  };

  // Closed + typing: change the value in place, like a native select.
  const commitQuietly = (item) => {
    const sel = nativeRef.current;
    if (!sel || sel.value === item.value) return;
    const setter = nativeValueSetter();
    if (setter) setter.call(sel, item.value);
    else sel.value = item.value;
    sel.dispatchEvent(new Event("input", { bubbles: true }));
    sel.dispatchEvent(new Event("change", { bubbles: true }));
    sync();
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    const key = e.key;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(key)) {
        e.preventDefault();
        openMenu(key === "ArrowUp" ? "last" : undefined);
      } else if (key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        typeahead(key);
      }
      return;
    }
    if (key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (key === "Home") { e.preventDefault(); setActive(list.findIndex((i) => !i.disabled)); }
    else if (key === "End") { e.preventDefault(); for (let i = list.length - 1; i >= 0; i--) if (!list[i].disabled) { setActive(i); break; } }
    else if (key === "Enter" || (key === " " && e.target === triggerRef.current)) { e.preventDefault(); commit(list[active]); }
    else if (key === "Escape") {
      // Handled here: a dialog behind this menu must not close as well.
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
      close(true);
    } else if (key === "Tab") { close(false); }
    else if (key.length === 1 && e.target === triggerRef.current && !e.metaKey && !e.ctrlKey && !e.altKey) { typeahead(key); }
  };

  // Mirror focus and blur onto the real select so onFocus / onBlur props (and
  // react-hook-form's touched state) behave as they did on the native control.
  const forward = (type) => {
    const sel = nativeRef.current;
    if (!sel || typeof FocusEvent === "undefined") return;
    sel.dispatchEvent(new FocusEvent(type, { bubbles: true }));
  };

  if (isNativeOnly) {
    return (
      <select
        ref={setNativeRef}
        {...rest}
        size={numericSize ? sizeProp : undefined}
        className={cx("lk-select-native-fallback", kept)}
        style={style}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {options ? renderOptionElements(options, placeholder) : children}
      </select>
    );
  }

  let lastGroup = null;
  const menu = open && pos ? (
    <div
      ref={menuRef}
      className={cx("lk-select__menu", pos.upward && "is-above")}
      style={{
        left: pos.left,
        top: pos.upward ? undefined : pos.top,
        bottom: pos.upward ? pos.bottom : undefined,
        minWidth: pos.minWidth,
        maxWidth: pos.maxWidth,
        maxHeight: pos.maxHeight,
      }}
      onMouseDown={(e) => { if (e.target !== searchRef.current) e.preventDefault(); }}
    >
      {useSearch && (
        <input
          ref={searchRef}
          className="lk-select__search"
          type="text"
          value={query}
          placeholder="Search"
          aria-label="Search options"
          aria-controls={ids.list}
          aria-activedescendant={active >= 0 ? `${ids.list}-${active}` : undefined}
          onChange={(e) => { setQuery(e.target.value); setActive(0); }}
          onKeyDown={onKeyDown}
        />
      )}
      <ul ref={listRef} id={ids.list} role="listbox" className="lk-select__list" aria-labelledby={labelledBy || ariaLabelledBy}>
        {list.length === 0 && <li className="lk-select__empty">No matches</li>}
        {list.map((item, i) => {
          const header = item.group && item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const isSel = item.index === selectedIndex;
          return (
            <React.Fragment key={`${item.index}`}>
              {header && <li className="lk-select__group" role="presentation">{header}</li>}
              <li
                id={`${ids.list}-${i}`}
                data-i={i}
                role="option"
                aria-selected={isSel}
                aria-disabled={item.disabled || undefined}
                className={cx("lk-select__option", isSel && "is-selected", i === active && "is-active", item.disabled && "is-disabled", item.badge && "has-badge")}
                onMouseEnter={() => !item.disabled && setActive(i)}
                onClick={() => commit(item)}
              >
                {isSel ? <Check /> : <span />}
                <span>
                  <span className="lk-select__label">{item.label}</span>
                  {item.description ? <span className="lk-select__desc">{item.description}</span> : null}
                </span>
                <Badge text={item.badge} tone={item.badgeTone} />
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <span
      ref={wrapRef}
      className={cx("lk-select", `lk-select--${size}`, isBlock && "lk-select--block", showInvalid && "is-invalid", open && "is-open", kept)}
      style={isHidden ? { ...layoutStyle(style), display: "none" } : layoutStyle(style)}
    >
      <select
        ref={setNativeRef}
        {...rest}
        className="lk-select__native"
        tabIndex={-1}
        aria-hidden="true"
        // Kept on the hidden select too, so code and tests that find a select
        // by its label (querySelector('select[aria-label=...]')) still do.
        aria-label={ariaLabel}
      >
        {options ? renderOptionElements(options, placeholder) : children}
      </select>
      <button
        ref={triggerRef}
        id={ids.trigger}
        type="button"
        className="lk-select__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? ids.list : undefined}
        aria-activedescendant={open && active >= 0 && !useSearch ? `${ids.list}-${active}` : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy || labelledBy}
        aria-invalid={showInvalid || undefined}
        title={rest.title}
        disabled={disabled}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={onKeyDown}
        onFocus={() => forward("focusin")}
        onBlur={() => { if (!open) forward("focusout"); }}
      >
        <span className={cx("lk-select__value", isPlaceholder && "is-placeholder")}>
          {display || " "}
          {selected && !isPlaceholder ? <Badge text={selected.badge} tone={selected.badgeTone} /> : null}
        </span>
        {!isBlock && (
          <span className="lk-select__sizer" aria-hidden="true">
            {visible.slice(0, SIZER_LIMIT).map((i) => <span key={i.index}>{i.label}</span>)}
          </span>
        )}
        <Chevron />
      </button>
      {menu && ReactDOM.createPortal(menu, portalHostFor(wrapRef.current))}
    </span>
  );
});

export default AppSelect;
